/**
 * Cache Manager Utility
 * 
 * Use this utility to invalidate Redux cache from anywhere in the app
 * Example: After creating/updating/deleting clients, call invalidateMainPageCache()
 */

import { store, persistor } from '../store';
import { RESET_APP_STATE } from '../store/reducers';
import { clearMainPageCache, forceRefresh } from '../store/reducers/mainPageDataSlice';
import { resetClientData } from '../store/reducers/clientDataSlice';
import { resetFinancialModal } from '../store/reducers/financialModalSlice';
import { resetLedgerReport } from '../store/reducers/ledgerReportSlice';
import { clearAllWarnings } from '../store/reducers/postponedChecksSlice';
import { resetDocumentPage } from '../store/reducers/documentPageSlice';
import { clearDocumentEnums, upsertAccountCard } from '../store/reducers/documentEnumsSlice';
import { resetJournalEntryPage } from '../store/reducers/journalEntryPageSlice';
import { resetPaymentAcceptancePage } from '../store/reducers/paymentAcceptancePageSlice';
import { clearPaymentAcceptanceEnums } from '../store/reducers/paymentAcceptanceEnumsSlice';
import { resetTransactionDocumentPage } from '../store/reducers/transactionDocumentPageSlice';
import { resetAccountCardsTablePage } from '../store/reducers/accountCardsTablePageSlice';
import { clearCache as clearDocumentCountsCache } from '../store/reducers/documentCountsSlice';

/**
 * Invalidate main page cache for a specific accountant or all accountants
 * This forces the Main page to refetch data on next visit
 * 
 * @param {string|null} accountantId - The accountant ID to clear cache for, or null for all
 */
export const invalidateMainPageCache = (accountantId = null) => {
  console.log('🗑️ [CacheManager] Invalidating main page cache:', accountantId || 'all accountants');
  
  if (accountantId) {
    store.dispatch(clearMainPageCache({ accountantId }));
  } else {
    store.dispatch(clearMainPageCache({}));
  }
};

/**
 * Force refresh the main page data
 * Similar to invalidateMainPageCache but with different intent
 * 
 * @param {string|null} accountantId - The accountant ID to refresh, or null for all
 */
export const forceRefreshMainPage = (accountantId = null) => {
  console.log('🔄 [CacheManager] Force refreshing main page:', accountantId || 'all accountants');
  
  if (accountantId) {
    store.dispatch(forceRefresh({ accountantId }));
  } else {
    store.dispatch(forceRefresh({}));
  }
};

/**
 * Keep Journal Entries / Documents debit-credit options in sync after an
 * account card is created or edited. If this case has no enums cache yet,
 * the next fetchDocumentEnums call loads the full list from the API.
 */
export const syncAccountCardToDocumentEnums = (caseId, card, previousCardId = null) => {
  if (!caseId || card?.cardId == null || card.cardId === '') return;
  store.dispatch(upsertAccountCard({ caseId, card, previousCardId }));
  store.dispatch(clearPaymentAcceptanceEnums(caseId));
};

export const invalidateDocumentEnums = (caseId = null) => {
  store.dispatch(clearDocumentEnums(caseId));
};

/**
 * Reset all client-specific Redux state.
 *
 * Call this when navigating from one client to another (e.g. clicking a client
 * row on the Main page) so the new client's screens never display stale data
 * from a previously opened client.
 *
 * Note: this intentionally does NOT clear the main page client list cache
 * (mainPageDataSlice / documentCountsSlice) since that data is not tied to a
 * specific client.
 */
export const resetAllClientReduxData = () => {
  console.log('🧹 [CacheManager] Resetting all client-specific redux state');
  const { dispatch } = store;

  dispatch(resetClientData());
  dispatch(resetFinancialModal());
  dispatch(resetLedgerReport());
  dispatch(clearAllWarnings());
  dispatch(resetDocumentPage());
  dispatch(clearDocumentEnums());
  dispatch(resetJournalEntryPage());
  dispatch(resetPaymentAcceptancePage());
  dispatch(clearPaymentAcceptanceEnums());
  dispatch(resetTransactionDocumentPage());
  dispatch(resetAccountCardsTablePage());
};

/**
 * Wipe all Redux state + redux-persist storage.
 * Call on logout so a different user never sees the previous session's data.
 */
export const clearAllAppStateOnLogout = async () => {
  console.log('🧹 [CacheManager] Clearing all app state on logout');

  try {
    persistor.pause();
  } catch (_) {
    /* ignore */
  }

  store.dispatch({ type: RESET_APP_STATE });
  // Also clear document-counts / main caches explicitly in case persist rehydrates mid-flight
  store.dispatch(clearMainPageCache({}));
  store.dispatch(clearDocumentCountsCache({}));

  try {
    await persistor.purge();
  } catch (error) {
    console.error('[CacheManager] persistor.purge failed:', error);
    localStorage.removeItem('persist:root');
  }

  try {
    persistor.persist();
  } catch (_) {
    /* ignore */
  }
};

export default {
  invalidateMainPageCache,
  forceRefreshMainPage,
  syncAccountCardToDocumentEnums,
  invalidateDocumentEnums,
  resetAllClientReduxData,
  clearAllAppStateOnLogout,
};
